import { Link } from "react-router-dom";
import { Tag, ChevronRight } from "lucide-react";
import { classifySpecial } from "../lib/promos";

/**
 * Per-line inline deal badge. Shows up under a cart line's product info so
 * the shopper sees exactly which offer that specific line is participating
 * in. Small radial-progress icon + green deal short name + chevron, all
 * routing to /menu/{store}/special/{id}.
 */
export default function CartLineDealBadges({ item, specials, progresses, storeSlug }) {
    if (!specials?.length) return null;
    const variant =
        (item.product?.variants || []).find((v) => v.option === item.option) ||
        (item.product?.variants || [])[0];
    if (!variant) return null;
    const ids = new Set([
        ...(variant.specialConditionIds || []),
        ...(variant.specialRewardIds || []),
    ]);
    if (ids.size === 0) return null;

    const progByDeal = Object.fromEntries(
        (progresses || []).map((p) => [p.id, p])
    );

    // For every deal this line is eligible for, render one badge.
    const badges = [];
    for (const s of specials) {
        if (!ids.has(s.id)) continue;
        const c = classifySpecial(s);
        if (!c) continue;
        const prog = progByDeal[s.id];
        // Threshold + current for radial; default to full if Dutchie already
        // qualified (no entry in progresses array).
        const threshold = Math.max(1, c.threshold || 1);
        const current = prog ? prog.current : threshold;
        const pct = Math.min(100, (current / threshold) * 100);
        const label = c.short || c.name;
        badges.push({ id: s.id, label, pct });
    }
    if (badges.length === 0) return null;

    const radius = 6;
    const circumference = 2 * Math.PI * radius;

    return (
        <div data-testid={`cart-line-deals-${item.id}`} className="space-y-1 mb-2.5">
            {badges.map((b) => {
                const dashoffset = circumference - (b.pct / 100) * circumference;
                return (
                    <Link
                        key={b.id}
                        to={`/menu/${storeSlug}/special/${b.id}`}
                        data-testid={`cart-line-deal-${item.id}-${b.id}`}
                        className="group inline-flex items-center gap-1.5 text-zen-olive-dark hover:text-zen-olive transition-colors max-w-full"
                    >
                        <span className="relative shrink-0 w-4 h-4">
                            <svg
                                viewBox="0 0 16 16"
                                className="w-4 h-4 -rotate-90"
                                aria-hidden="true"
                            >
                                <circle
                                    cx="8"
                                    cy="8"
                                    r={radius}
                                    fill="none"
                                    stroke="rgba(107,124,79,0.25)"
                                    strokeWidth="1.5"
                                />
                                <circle
                                    cx="8"
                                    cy="8"
                                    r={radius}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={dashoffset}
                                />
                            </svg>
                            <Tag
                                size={7}
                                strokeWidth={2.5}
                                className="absolute inset-0 m-auto"
                            />
                        </span>
                        <span className="text-xs font-medium tracking-wide truncate">
                            {b.label}
                        </span>
                        <ChevronRight
                            size={12}
                            strokeWidth={2}
                            className="shrink-0 transition-transform group-hover:translate-x-0.5"
                        />
                    </Link>
                );
            })}
        </div>
    );
}
